import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Save, Upload, X, FileText, Video, Music, Image as ImageIcon,
  AlertCircle, CheckCircle2, Loader2, ImageDown, ExternalLink,
} from 'lucide-react'
import RichEditor from '../../components/RichEditorLazy'
import { contentApi, type ContentAttachment, type ContentCategory, type ContentDetail, type ContentAudience } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { sanitizeHtml, isBlankHtml } from '../../lib/sanitize'
import { isHttpUrl, isWechatArticleUrl } from '../../lib/externalLink'
import { useContentBasePath } from '../../lib/contentRoutes'
import ContentPhonePreview from './ContentPhonePreview'

const mediaLabel = {
  text: '图文',
  video: '视频',
  audio: '音频',
}

/** 取富文本里第一张图片的地址（正文首图） */
const firstImageUrlInBody = (html: string): string | null => {
  if (!html) return null
  const m = html.match(/<img\b[^>]*\bsrc=["']([^"']+)["']/i)
  return m ? m[1] : null
}

/**
 * 判断两个图片地址是否指向同一张图。
 * 正文里的 `src` 可能是 `/static/uploads/x.jpg`，而附件 `file_url` 可能是绝对地址
 * （或反之），所以用 pathname 比较，避免因为域名/协议差异匹配不上。
 */
const isSameImage = (a: string, b: string): boolean => {
  if (!a || !b) return false
  if (a === b) return true
  const norm = (u: string) => {
    try {
      return new URL(u, window.location.origin).pathname
    } catch {
      return u
    }
  }
  return norm(a) === norm(b)
}

const defaultPublishAt = () => {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

const ContentEditor = () => {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  // 编辑器在两个入口下复用：管理端 /admin/content、医护端 /medical/author。
  // 保存/提交后的回跳必须落在当前入口，否则医护被甩进管理端布局。
  const basePath = useContentBasePath()
  // 本编辑器同时服务总后台与医护投稿人（内容投稿权限开放）：
  //   admin → 可直接保存并发布，可用全部高级字段（推荐、排序、定时发布等）
  //   医护  → 只能保存草稿或提交审核，高级字段与发布权限不可见
  const isAdmin = user?.role === 'admin'
  const isEdit = !!id && id !== 'new'

  const [categories, setCategories] = useState<ContentCategory[]>([])
  const [audiences, setAudiences] = useState<ContentAudience[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null)
  const [uploading, setUploading] = useState<{ name: string; percent: number } | null>(null)
  /**
   * 「公众号原文模式」：导入公众号文章后，正文直接存原文 HTML，**不经过富文本编辑器**。
   *
   * 为什么必须这样：wangEditor 是模型化编辑器，只认得自己有模型的标签。实测把公众号
   * 原文（158 个 section、249 处内联样式、14 张图）交给它 setHtml，出来只剩
   * <p>/<span> 纯文本（54K → 1K），排版与图片全没了 —— 用户要的是「和公众号排版一致」，
   * 所以原文模式把它挡在编辑器之外，只做只读预览，保存的就是原文。
   *
   * 退出方式：点「在编辑器中编辑」= 明确接受排版会被归一（弹确认），切回编辑器模式。
   */
  const [rawBody, setRawBody] = useState<string | null>(null)
  /** 「提取正文首图」进行中（要等一次认领请求，按钮上给出反馈） */  const [extracting, setExtracting] = useState(false)
  const initialFormRef = useRef<any>(null)
  const dirtyRef = useRef(false)
  /**
   * 用户本次编辑中手动清空过封面 → 之后不再自动用正文首图兜底。
   * 没有这个标记，「删除封面」会失效：正文里还有图，effect 会立刻又填回来。
   */
  const coverClearedRef = useRef(false)
  /**
   * 自动兜底已经对哪些图片地址**发起过认领请求**（只用于去掉重复请求）。
   * ⚠️ 只在实际调用接口前记录，不要记在「本地查找失败」时 ——
   *    编辑页刚打开时 attachments 可能还没加载完，那时本地必然查不到，
   *    若就此认定「试过了」，封面就永远填不上了（曾因此让 U6/U7 回归失败）。
   */
  const claimTriedRef = useRef<Set<string>>(new Set())
  const dupState = (location.state as any)?.duplicate ? (location.state as any) : null

  const [form, setForm] = useState({
    title: '',
    summary: '',
    category_id: '',
    audience_id: '',
    media_type: 'text' as 'text' | 'video' | 'audio',
    body: '',
    author: '',
    source: '',
    county_code: '',
    plan_month: '',
    scheduled_at: '',
    auto_offline_at: '',
    published_at: defaultPublishAt(),
    is_recommended: 0,
    sort_order: 0,
    cover_attachment_id: '',
    link_url: '',
    tag_ids: [] as string[],
  })
  const [attachments, setAttachments] = useState<ContentAttachment[]>([])
  const [coverAttachment, setCoverAttachment] = useState<ContentAttachment | null>(null)
  const [allTags, setAllTags] = useState<{ id: string; name: string; slug: string; use_count: number; is_active: number }[]>([])
  const [preview, setPreview] = useState(false)
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>('basic')

  useEffect(() => {
    // 作者端有独立的字典接口：只返回启用分类，且非投稿人访问会被拒
    const catFn = isAdmin ? contentApi.adminListCategoriesAll : contentApi.authorCategories
    const audFn = isAdmin ? contentApi.adminAudiences : contentApi.authorAudiences
    catFn().then(setCategories).catch(() => setCategories([]))
    audFn().then(setAudiences).catch(() => setAudiences([]))
    // 标签字典仍由总后台维护，作者端暂不提供标签选择入口
    const tagFn: Promise<any> = isAdmin
      ? contentApi.adminListTags({ page_size: 100, is_active: 1 })
      : Promise.resolve({ items: [] })
    tagFn.then((r: any) => setAllTags(r.items || [])).catch(() => setAllTags([]))
    if (dupState) {
      setForm(prev => ({
        ...prev,
        title: dupState.title || '',
        summary: dupState.summary || '',
        category_id: dupState.category_id || prev.category_id,
        audience_id: dupState.audience_id || prev.audience_id,
        media_type: dupState.media_type || prev.media_type,
        author: dupState.author || prev.author,
      }))
      setToast({ kind: 'success', msg: '已加载副本基础信息，请补充内容后保存' })
    }
  }, [isAdmin])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3500)
    return () => clearTimeout(t)
  }, [toast])

  useEffect(() => {
    if (!isEdit) return
    setLoading(true)
    const getFn = isAdmin ? contentApi.adminGetContent : contentApi.authorGetContent
    getFn(id!)
      .then((d: ContentDetail) => {
        setForm({
          title: d.title || '',
          summary: d.summary || '',
          category_id: d.category_id,
          audience_id: d.audience_id || '',
          media_type: d.media_type,
          body: d.body || '',
          author: d.author || '',
          source: d.source || '',
          county_code: d.county_code || '',
          plan_month: d.plan_month || '',
          scheduled_at: d.scheduled_at ? new Date(d.scheduled_at).toISOString().slice(0, 16) : '',
          auto_offline_at: d.auto_offline_at ? new Date(d.auto_offline_at).toISOString().slice(0, 16) : '',
          published_at: d.published_at ? new Date(d.published_at).toISOString().slice(0, 16) : defaultPublishAt(),
          is_recommended: d.is_recommended,
          sort_order: d.sort_order,
          cover_attachment_id: d.cover_attachment_id || '',
          link_url: d.link_url || '',
          tag_ids: [],
        })
        const loadedAttachments = d.attachments || []
        setAttachments(loadedAttachments)
        setCoverAttachment(
          loadedAttachments.find(a => a.id === d.cover_attachment_id) || null,
        )
        /* 已导入的公众号原文稿（正文含 <section>，公众号排版的标志）→ 自动进原文模式。
           ⚠️ 不这么做的话，编辑器一挂载就会把原文按自己的模型归一（section 与多数
           内联样式丢失）并通过 onChange 写回表单 —— 用户只是打开看一眼就把排版毁了。
           富文本编辑器产出的正文不含 section，所以这个判断不会误伤。 */
        if (d.body && /<section[\s>]/i.test(d.body)) {
          setRawBody(d.body)
        }
        // 加载内容已绑定的标签（标签为总后台维护，作者端不展示）
        if (isAdmin) {
          contentApi.adminGetContentTags(id!).then((ids) => {
            setForm((prev) => ({ ...prev, tag_ids: ids || [] }))
          }).catch(() => {})
        }
      })
      .catch(e => setError(e.message || '加载失败'))
      .finally(() => {
        setLoading(false)
        initialFormRef.current = JSON.stringify(form)
        dirtyRef.current = false
      })
  }, [id, isEdit, isAdmin])

  useEffect(() => {
    if (!initialFormRef.current) return
    dirtyRef.current = JSON.stringify(form) !== initialFormRef.current
  }, [form])

  /**
   * 原文模式自愈：form.body 被清空时用 rawBody 补回。
   *
   * 背景（2026-09-20 用户反馈截图）：出现「公众号标题栏在、正文框一片空白、
   * 左侧手机预览也没正文」的状态 —— rawBody 还在，但 form.body 被某条路径清成了空
   * （编辑器卸载时的一次延迟 onChange、或页面里其他表单重置）。空状态会让用户
   * 完全看不出发生了什么，且此时保存会把正文写空。这里直接以 rawBody 为准补回。
   */
  useEffect(() => {
    if (rawBody && !form.body) {
      setForm(f => ({ ...f, body: rawBody }))
    }
  }, [rawBody, form.body])

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      e.preventDefault()
      e.returnValue = '有未保存的修改，确定离开？'
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const onUpload = async (file: File, type: 'image' | 'video' | 'audio', isPrimary = false, retryAttempt = 0): Promise<ContentAttachment | null> => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('is_primary', String(isPrimary))
    if (isEdit) fd.append('content_id', id!)
    setUploading({ name: file.name, percent: 0 })
    try {
      const res: any = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', '/api/v1/upload/content-attachment')
        const tok = localStorage.getItem('access_token') || localStorage.getItem('token')
        if (tok) xhr.setRequestHeader('Authorization', 'Bearer ' + tok)
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100)
            setUploading(prev => prev ? { ...prev, percent: pct } : prev)
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)) } catch { reject(new Error('format error')) }
          } else {
            let msg = 'HTTP ' + xhr.status
            try { const j = JSON.parse(xhr.responseText); if (j && j.detail) msg = j.detail } catch {}
            reject(new Error(msg))
          }
        }
        xhr.onerror = () => reject(new Error('network error'))
        xhr.send(fd)
      })
      const newAtt: ContentAttachment = {
        id: res.id,
        content_id: id || '',
        attachment_type: type,
        file_name: file.name,
        file_url: res.file_url,
        mime_type: res.mime_type,
        file_size: res.file_size,
        sort_order: attachments.filter(a => a.attachment_type === type).length,
        is_primary: isPrimary ? 1 : 0,
        status: 'active',
      }
      setAttachments(prev => [...prev.filter(a => !(isPrimary && a.attachment_type === type && a.is_primary)), newAtt])
      setToast({ kind: 'success', msg: '上传成功' })
      setUploading(null)
      return newAtt
    } catch (e: any) {
      setUploading(null)
      const msg = e.message || '上传失败'
      if (retryAttempt < 1) {
        if (confirm(msg + '，是否重试？')) return onUpload(file, type, isPrimary, retryAttempt + 1)
      } else {
        setToast({ kind: 'error', msg })
      }
      return null
    }
  }

  /**
   * 把正文首图解析成「可用于封面的附件」。
   *
   * ⚠️ 不能只查 `attachments`（踩过这个坑）：
   *   正文图片走的是 `POST /api/v1/upload` —— 它**只把文件落到 static/uploads/，
   *   根本不创建 content_attachments 记录**（编辑器插图、Word 导入、粘贴图片
   *   全走这条路径）。而封面必须挂在附件记录上（contents.cover_attachment_id），
   *   于是「提取正文首图 / 自动填充封面」在正文图上必然找不到附件，
   *   一直误报「这张图不是本地上传的附件」。实测用户导入 Word 后就是这个现象。
   *
   * 所以这里分两步：
   *   1. 先在本内容已加载的 `attachments` 里找（命中的话零成本，不重复登记）
   *   2. 找不到就让后端按 URL「认领」——文件已经在服务器上了，只是补一条记录，
   *      不会重新上传；接口幂等，同一 URL 反复调用返回同一条记录。
   *
   * @param allowRetry 是否允许对同一地址重复发起认领。
   *   自动兜底路径传 false（去掉重），避免 onChange 频繁触发时反复打接口；
   *   「提取正文首图」按钮传 true，保证用户手动点击**永远能重试**
   *   （即使之前那次自动认领因为网络问题失败了）。
   */
  const resolveCoverFromBody = async (opts?: { allowRetry?: boolean }): Promise<ContentAttachment | null> => {
    const url = firstImageUrlInBody(form.body)
    if (!url) return null

    const local = attachments.find(a => a.attachment_type === 'image' && isSameImage(a.file_url, url))
    if (local) return local

    // 外链图（非本站静态资源）后端认领会拒绝，直接返回 null，让调用方给出提示
    if (!url.includes('/static/uploads/')) return null

    /* ⚠️ 「已尝试过」只在这里记 —— 不能记在本地查找失败时：
       编辑页刚打开时 attachments 可能还没加载完（form.body 先到），
       那时本地必然查不到，若就此认定「试过了」，等 attachments 到位后
       就不会再命中，封面永远填不上（会破坏「复用已有附件」这条路）。 */
    if (!opts?.allowRetry && claimTriedRef.current.has(url)) return null
    claimTriedRef.current.add(url)

    try {
      const claimed = await contentApi.claimAttachment(url)
      setAttachments(prev => (prev.some(a => a.id === claimed.id) ? prev : [...prev, claimed]))
      return claimed
    } catch {
      return null
    }
  }

  /** 「提取正文第一张图作为封面」按钮（仅覆盖封面，不动正文） */
  const extractCoverFromBody = async () => {
    if (!firstImageUrlInBody(form.body)) {
      setToast({ kind: 'error', msg: '正文里还没有图片，先插入或导入一张图' })
      return
    }
    setExtracting(true)
    const hit = await resolveCoverFromBody({ allowRetry: true })
    setExtracting(false)
    if (!hit) {
      setToast({ kind: 'error', msg: '这张图不在本地上传目录里（可能是外链图片），无法直接设为封面，请手动上传' })
      return
    }
    coverClearedRef.current = false
    setCoverAttachment(hit)
    setForm(f => ({ ...f, cover_attachment_id: hit.id }))
    setToast({ kind: 'success', msg: '已把正文第一张图设为封面' })
  }

  /**
   * 正文出现图片、且尚未设置封面时，自动把正文首图设为封面。
   * 「导入 Word」和「正文里上传/插入图片」最终都会体现在 form.body 上，
   * 所以在这里统一兜底即可，不必到处挂钩子。
   * 用户手动删过封面（coverClearedRef）则不再自动填。
   *
   * 依赖项里带 `attachments` 是必需的：编辑页打开时 form.body 往往先到、
   * attachments 后到，正文图属于「已有附件」的情形必须等 attachments 到位后
   * 再命中一次（去重逻辑放在 resolveCoverFromBody 内部，见那里的注释）。
   */
  useEffect(() => {
    if (form.cover_attachment_id) return
    if (coverClearedRef.current) return
    let cancelled = false
    void (async () => {
      const hit = await resolveCoverFromBody()
      if (cancelled || !hit) return
      setCoverAttachment(hit)
      setForm(f => (f.cover_attachment_id ? f : { ...f, cover_attachment_id: hit.id }))
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.body, form.cover_attachment_id, attachments])

  const onUploadCover = async (file: File) => {
    const previous = coverAttachment
    const uploaded = await onUpload(file, 'image')
    if (!uploaded) return
    if (previous) {
      await contentApi.deleteAttachment(previous.id).catch(() => {})
      setAttachments(prev => prev.filter(a => a.id !== previous.id))
    }
    setCoverAttachment(uploaded)
    setForm(f => ({ ...f, cover_attachment_id: uploaded.id }))
  }

  const removeCover = async () => {
    if (!coverAttachment) return
    if (!confirm('确认删除封面图？删除后不会影响正文图片。')) return
    // 标记「用户主动清空过封面」，避免自动兜底 logic 立刻把正文首图又填回来
    coverClearedRef.current = true
    await contentApi.deleteAttachment(coverAttachment.id).catch(() => {})
    setAttachments(prev => prev.filter(a => a.id !== coverAttachment.id))
    setCoverAttachment(null)
    setForm(f => ({ ...f, cover_attachment_id: '' }))
  }

  const removeAttachment = async (aid: string) => {
    if (aid === coverAttachment?.id) return removeCover()
    if (!confirm('确认删除该附件？')) return
    try {
      await contentApi.deleteAttachment(aid)
    } catch {}
    setAttachments(prev => prev.filter(a => a.id !== aid))
  }

  const validateForm = (publish: boolean) => {
    if (!form.title.trim()) { setActiveTab('basic'); setToast({ kind: 'error', msg: '请填写标题' }); return false }
    if (!form.category_id) { setActiveTab('basic'); setToast({ kind: 'error', msg: '请选择分类' }); return false }
    // 富文本空内容形如 `<p><br></p>`，不能用 trim() 判空（wangEditor 换装后改动）
    if (form.media_type === 'text' && isBlankHtml(form.body)) {
      setActiveTab('basic'); setToast({ kind: 'error', msg: '图文内容必须填写正文' }); return false
    }
    if (publish && form.media_type === 'video' && !videos.some(a => a.is_primary === 1)) {
      setActiveTab('basic'); setToast({ kind: 'error', msg: '视频内容必须上传主视频' }); return false
    }
    if (publish && form.media_type === 'audio' && !audios.some(a => a.is_primary === 1)) {
      setActiveTab('basic'); setToast({ kind: 'error', msg: '音频内容必须上传主音频' }); return false
    }
    return true
  }

  /** 作者端只接受白名单字段：总后台专属字段（推荐/排序/定时/发布时间）一律不传 */
  const authorPayload = () => ({
    category_id: form.category_id,
    audience_id: form.audience_id || null,
    media_type: form.media_type,
    title: form.title,
    summary: form.summary || null,
    cover_attachment_id: form.cover_attachment_id || null,
    body: form.body,
    authorship: form.author || null,
    source: form.source || null,
    county_code: form.county_code || null,
    /* ⚠️ 外链要原样传（含空串），不能用 `|| null`：
       作者端序列化用 exclude_none=True，None 会被丢掉，那样清空外链就失效了。
       空串透传给后端，由 content_service 归一成 NULL。 */
    link_url: form.link_url,
  })

  /**
   * mode：
   *   draft   → 仅保存（不提交、不发布）
   *   submit  → 医护投稿：保存并提交总后台审核
   *   publish → 总后台：保存并直接发布
   */
  const onSave = async (mode: 'draft' | 'submit' | 'publish') => {
    // 提交审核与发布都要求内容完整，走同一套严格校验
    const strict = mode !== 'draft'
    if (!validateForm(strict)) return
    if (strict) {
      if (form.media_type === 'video' && !attachments.some(a => a.attachment_type === 'video' && a.is_primary === 1)) {
        setToast({ kind: 'error', msg: '视频内容必须上传主视频' }); return
      }
      if (form.media_type === 'audio' && !attachments.some(a => a.attachment_type === 'audio' && a.is_primary === 1)) {
        setToast({ kind: 'error', msg: '音频内容必须上传主音频' }); return
      }
      if (form.media_type === 'text' && isBlankHtml(form.body) && !attachments.some(a => a.attachment_type === 'image')) {
        setToast({ kind: 'error', msg: '图文内容必须填写正文或上传图片' }); return
      }
    }
    setSaving(true)
    try {
      let contentId = id
      if (isEdit) {
        if (isAdmin) await contentApi.adminUpdateContent(id!, form)
        else await contentApi.authorUpdateContent(id!, authorPayload())
      } else {
        const created = isAdmin
          ? await contentApi.adminCreateContent(form)
          : await contentApi.authorCreateContent(authorPayload())
        contentId = created.id
        // 新建时附件先以未绑定状态上传，创建文章后再绑定正文附件和封面
        if (contentId && (form.cover_attachment_id || attachments.length > 0)) {
          const bindData = {
            cover_attachment_id: form.cover_attachment_id || null,
            attachment_ids: attachments.map(a => a.id),
          }
          if (isAdmin) await contentApi.adminBindContentAttachments(contentId, bindData)
          else await contentApi.authorBindAttachments(contentId, bindData)
        }
        nav(basePath + '/' + contentId, { replace: true })
      }
      // 保存后同步标签（仅总后台有标签选择入口）
      if (contentId && isAdmin) {
        await contentApi.adminSetContentTags(contentId, form.tag_ids || []).catch((e) => {
          console.warn('set tags failed', e)
        })
      }
      dirtyRef.current = false
      if (mode === 'publish' && contentId) {
        await contentApi.adminPublishContent(contentId)
        setToast({ kind: 'success', msg: '已发布' })
        setTimeout(() => nav(basePath), 600)
      } else if (mode === 'submit' && contentId) {
        await contentApi.authorSubmitReview(contentId)
        setToast({ kind: 'success', msg: '已提交审核，通过后将在用户端展示' })
        setTimeout(() => nav(basePath), 900)
      } else {
        initialFormRef.current = JSON.stringify(form)
        setToast({ kind: 'success', msg: '已保存草稿' })
      }
    } catch (e: any) {
      setToast({ kind: 'error', msg: e.message || '保存失败' })
    } finally {
      setSaving(false)
    }
  }

  const videos = attachments.filter(a => a.attachment_type === 'video')
  const audios = attachments.filter(a => a.attachment_type === 'audio')

  /* 分类名（供左侧预览展示：一级 + 二级各取一个） */
  const currentCategory = categories.find(c => c.id === form.category_id)
  const currentCategoryName = currentCategory?.name || ''
  const parentCategoryName = currentCategory?.parent_id
    ? (categories.find(c => c.id === currentCategory.parent_id)?.name || '')
    : ''

  return (
    <div className="p-4 sm:p-6">
      {/* 左侧手机预览 + 右侧编辑表单。预览按需求放在左侧；
          xl 以下自动隐藏，避免窄屏把表单挤成一条。

          布局要点：
          - `items-start` 必须留着：sticky 的副作用需要"项目不被拉伸"才成立，
            否则 flex 会把 aside 拉满行高，没有可粘的行程。
          - `sticky top-4` —— 正文往下滚时预览跟着走，始终留在视区。
            ⚠️ 它依赖祖先链上没有「自己不滚动的滚动容器」，Layout 里那层
               `overflow-x` 必须是 `clip` 而非 `hidden`，否则 sticky 会失效。
          - **高度用 min + max 夹住**（2026-09-19 追加）：
            不给束缚时，预览高度完全由内容撑开 —— 空内容只有 364px（手机壳被压扁，
            看着很怪），正文长了又会一路涨到贴满屏幕。
            · min 600px：内容再少也保持一台手机的基本体量（壳内滚动区 ≈478px）
            · max 700px：正文再长也封顶，超出部分在壳内滚动
            ⚠️ 两者都必须包一层 `min(..., calc(100vh - 2rem))`：
               CSS 里 **min-height 的优先级高于 max-height**，若写死 600px，
               在矮屏上会顶穿 max-height 把预览挤出屏幕。包了之后
               视窗 <632px 时 min 与 max 同时收敛到 (100vh - 2rem)，高度退化成一个固定值。
          - aside 自身 `flex` 是为了让子项按剩余高度拉伸，把 min/max 传下去。

          注：data-testid 供回归脚本定位 —— 后台 Layout 自己也有一个 <aside> 侧边栏，
          用标签名选择会选错。 */}
      <div className="flex gap-6 items-start">
        <aside
          className="hidden xl:flex sticky top-4 min-h-[min(600px,calc(100vh_-_2rem))] max-h-[min(700px,calc(100vh_-_2rem))]"
          data-testid="phone-preview"
        >
          <ContentPhonePreview
            title={form.title}
            summary={form.summary}
            categoryName={currentCategoryName}
            parentCategoryName={parentCategoryName}
            audienceName={audiences.find(a => a.id === form.audience_id)?.name || ''}
            author={form.author}
            source={form.source}
            bodyHtml={rawBody || form.body}
            mediaType={form.media_type}
            mediaUrl={attachments.find(a => a.is_primary === 1 && a.attachment_type === form.media_type)?.file_url || null}
            linkUrl={form.link_url}
            recommended={form.is_recommended === 1}
          />
        </aside>

        <div className="flex-1 min-w-0 max-w-5xl space-y-4">
      {toast && (
        <div className={'fixed top-20 right-6 z-50 px-4 py-2 rounded shadow-lg flex items-center gap-2 text-sm ' + (toast.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200')}>
          {toast.kind === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span>{toast.msg}</span>
          <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600"><X className="w-3 h-3" /></button>
        </div>
      )}
      {uploading && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white border rounded px-4 py-2 shadow-lg flex items-center gap-2 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-green-600" />
          <span className="truncate max-w-[200px]">{uploading.name}</span>
          <div className="w-32 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div className="bg-green-600 h-2 transition-all" style={{ width: uploading.percent + '%' }} />
          </div>
          <span className="tabular-nums">{uploading.percent}%</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isEdit
            ? (isAdmin ? '编辑内容' : '编辑投稿')
            : (isAdmin ? '新建内容' : '投稿撰写')}
        </h1>
        <Link to={basePath} className="text-sm text-gray-500 hover:underline">返回列表</Link>
      </div>
      {loading && <div className="text-gray-400 text-sm">加载中…</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

      <div className="bg-white border rounded-lg p-1 flex gap-1">
        {([
          ['basic', '基础信息', '标题、分类、正文与媒体'],
          ['advanced', '高级设置', '标签、来源、推荐与时间'],
        ] as const).map(([key, label, hint]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`flex-1 text-left px-3 py-2 rounded-md transition-colors ${activeTab === key ? 'bg-green-50 text-green-700' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <span className="block text-sm font-medium">{label}</span>
            <span className="block text-[11px] mt-0.5 opacity-70">{hint}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="标题" required colSpan={3} hidden={activeTab !== 'basic'}>
          <input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="请输入内容标题"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </Field>

        <Field label="一级分类" required hidden={activeTab !== 'basic'}>
          <select
            value={(() => {
              const c = categories.find(x => x.id === form.category_id)
              if (c && c.parent_id) return c.parent_id
              return form.category_id
            })()}
            onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            <option value="">请选择</option>
            {categories.filter(c => c.level === 1).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="二级分类" hidden={activeTab !== 'basic'}>
          <select
            value={form.category_id}
            onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            <option value="">无（仅归属一级）</option>
            {categories.filter(c => c.level === 2 && c.parent_id === (() => {
              const cat = categories.find(x => x.id === form.category_id)
              return cat?.parent_id || form.category_id
            })()).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="媒体类型" required hidden={activeTab !== 'basic'}>
          <div className="flex items-center gap-5 h-[38px]">
            {([['text', '图文'], ['video', '视频'], ['audio', '音频']] as const).map(([value, label]) => (
              <label key={value} className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="media_type"
                  value={value}
                  checked={form.media_type === value}
                  onChange={() => setForm(f => ({ ...f, media_type: value }))}
                  className="accent-green-600"
                />
                {label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="目标人群" hidden={activeTab !== 'advanced'}>
          <select value={form.audience_id} onChange={e => setForm(f => ({ ...f, audience_id: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">不限</option>
            {audiences.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        <Field label="内容标签" hint={`已选 ${form.tag_ids.length} / ${allTags.length}`} hidden={activeTab !== 'advanced' || !isAdmin}>
          <div className="border rounded px-2 py-2 max-h-32 overflow-y-auto bg-gray-50 flex flex-wrap gap-1.5">
            {allTags.length === 0 && <span className="text-xs text-gray-400">暂无可用标签</span>}
            {allTags.map(t => {
              const active = form.tag_ids.includes(t.id)
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setForm(f => ({
                    ...f,
                    tag_ids: active ? f.tag_ids.filter(x => x !== t.id) : [...f.tag_ids, t.id],
                  }))}
                  className={'text-xs px-2 py-1 rounded-full border transition-colors ' + (active
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-green-400')}
                >
                  #{t.name}
                </button>
              )
            })}
          </div>
        </Field>

        {/* 排序 / 推荐 / 发布时间 / 定时与自动下线：属总后台运营配置，医护端不展示 */}
        <Field label="排序" hidden={activeTab !== 'advanced' || !isAdmin}>
          <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>

        <Field label={isAdmin ? '作者' : '署名'} colSpan={1} hidden={activeTab !== 'advanced'}>
          <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>
        <Field label="来源" hidden={activeTab !== 'advanced'}>
          <input value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>
        <Field label="推荐" hidden={activeTab !== 'advanced' || !isAdmin}>
          <select value={form.is_recommended} onChange={e => setForm(f => ({ ...f, is_recommended: Number(e.target.value) }))} className="w-full border rounded px-3 py-2 text-sm">
            <option value={0}>否</option>
            <option value={1}>是</option>
          </select>
        </Field>

        <Field label="发布时间" hint="默认当前时间" hidden={activeTab !== 'advanced' || !isAdmin}>
          <input type="datetime-local" value={form.published_at} onChange={e => setForm(f => ({ ...f, published_at: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>
        <Field label="定时发布" hidden={activeTab !== 'advanced' || !isAdmin}>
          <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>
        <Field label="自动下线" hidden={activeTab !== 'advanced' || !isAdmin}>
          <input type="datetime-local" value={form.auto_offline_at} onChange={e => setForm(f => ({ ...f, auto_offline_at: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>

        {/* 外链字段（2026-09-19 隐藏编辑入口，只藏不删）：
            功能实测不好用，先停掉新建/编辑入口；数据层与用户端跳转逻辑全部保留 ——
            form 里 link_url 仍会原样提交（authorPayload/adminPayload 均透传），
            存量已配置外链的内容照常跳转。重新开放只需解除本段注释。
            语义与跳转约定见 lib/externalLink.ts。
            放在「高级设置」tab 末尾：医护端的 admin 字段都是隐藏的，
            放在前面会被顶上去，放在末尾则各角色布局都稳定。
        <Field
          label="外链地址"
          colSpan={3}
          hidden={activeTab !== 'advanced'}
          hint="如果填写外链，就不会展示详情内容 —— 用户点击这篇文章会直接跳转"
        >
          <input
            value={form.link_url}
            onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
            placeholder="https://mp.weixin.qq.com/s/xxxxxxxx"
            className={'w-full border rounded px-3 py-2 text-sm ' + (form.link_url && !isHttpUrl(form.link_url) ? 'border-red-300 bg-red-50/40' : '')}
          />
          {form.link_url ? (
            !isHttpUrl(form.link_url) ? (
              <p className="text-[11px] text-red-600 mt-1 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />需以 http:// 或 https:// 开头的完整网址
              </p>
            ) : isWechatArticleUrl(form.link_url) ? (
              <p className="text-[11px] text-green-700 mt-1 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />公众号文章链接：用户点击可直接打开
              </p>
            ) : (
              <p className="text-[11px] text-amber-600 mt-1 inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />非公众号链接：微信内会弹窗提示用户复制到浏览器打开（普通浏览器则直接打开）
              </p>
            )
          ) : null}
        </Field>
        外链字段注释结束 */}

        <Field label="摘要" colSpan={3} hidden={activeTab !== 'basic'}>
          <textarea value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} rows={2} className="w-full border rounded px-3 py-2 text-sm" />
        </Field>
      </div>

      {activeTab === 'basic' && (
        <CoverUploader
          item={coverAttachment}
          onUpload={onUploadCover}
          onRemove={removeCover}
          onExtract={extractCoverFromBody}
          canExtract={!!firstImageUrlInBody(form.body)}
          extracting={extracting}
        />
      )}

      {activeTab === 'basic' && form.media_type === 'text' && (
        <Field label="正文" required as="div">
          {rawBody ? (
            /* 公众号原文模式：只读预览 + 退出入口。
               直接渲染原文（含 section 与内联样式），与用户端详情页同一套 .content-body 排版。
               ⚠️ 渲染 rawBody 而不是 form.body：rawBody 是「原文」的唯一来源，
               form.body 可能被其它路径清空（见上面的自愈 effect），
               以 rawBody 渲染可保证「只要有原文就一定看得见」。 */
            <div className="border border-green-200 rounded-xl overflow-hidden">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 bg-green-50 border-b border-green-100">
                <span className="text-xs font-medium text-green-800 inline-flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" />
                  公众号原文（排版原样保存）
                  <span className="text-green-700/70 font-normal">
                    · {rawBody.length.toLocaleString()} 字符
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    /* 进编辑器 = wangEditor 会按自己的模型归一排版（section 与多数
                       内联样式会丢失），这一步不可逆，先说清楚 */
                    if (window.confirm('进入富文本编辑器后，公众号的原排版（分段样式、图片位置等）会被编辑器归一，无法还原。确定继续吗？')) {
                      setRawBody(null)
                    }
                  }}
                  className="ml-auto text-xs text-gray-600 hover:text-gray-900 underline"
                >
                  在编辑器中编辑
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('确定清除已导入的正文吗？')) {
                      setRawBody(null)
                      setForm(f => ({ ...f, body: '' }))
                    }
                  }}
                  className="text-xs text-red-600 hover:text-red-700 underline"
                >
                  清除正文
                </button>
              </div>
              <div
                className="content-body prose prose-sm max-w-none break-words text-gray-800 p-4 max-h-[560px] overflow-y-auto thin-scroll"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(rawBody) }}
              />
            </div>
          ) : (
            <RichEditor
              value={form.body}
              onChange={(v: string) => setForm(f => ({ ...f, body: v }))}
              placeholder="支持文字、图片、标题、列表…"
              onWechatImported={({ title, summary, html }) => {
                setForm(f => ({
                  ...f,
                  title: title || f.title,
                  summary: summary || f.summary,
                  body: html,
                }))
                setRawBody(html)
              }}
            />
          )}
        </Field>
      )}

      {activeTab === 'basic' && (form.media_type === 'video' || form.media_type === 'audio') && (
        <Field label={mediaLabel[form.media_type] + '介绍'} as="div">
          <RichEditor
            value={form.body}
            onChange={(v: string) => setForm(f => ({ ...f, body: v }))}
            placeholder="可填写图文介绍"
            onWechatImported={({ summary }) => setForm(f => ({ ...f, summary: summary || f.summary }))}
          />
        </Field>
      )}

      {activeTab === 'basic' && form.media_type === 'video' && (
        <AttachmentUploader
          label="主视频（必填）"
          type="video"
          items={videos}
          onUpload={file => onUpload(file, 'video', true)}
          onRemove={removeAttachment}
        />
      )}
      {activeTab === 'basic' && form.media_type === 'audio' && (
        <AttachmentUploader
          label="主音频（必填）"
          type="audio"
          items={audios}
          onUpload={file => onUpload(file, 'audio', true)}
          onRemove={removeAttachment}
        />
      )}

      <div className="flex items-center gap-2 sticky bottom-0 bg-white/80 backdrop-blur p-3 border rounded">
        <button disabled={saving} onClick={() => onSave('draft')} className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50">
          <Save className="w-4 h-4" /> 保存草稿
        </button>
        {isAdmin ? (
          <button disabled={saving} onClick={() => onSave('publish')} className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            <Save className="w-4 h-4" /> 保存并发布
          </button>
        ) : (
          <button disabled={saving} onClick={() => onSave('submit')} className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> 保存并提交审核
          </button>
        )}
        {!isAdmin && (
          <span className="text-xs text-gray-500 hidden sm:inline">提交后由总后台审核，通过后在用户端展示</span>
        )}
        <button onClick={() => setPreview(p => !p)} className="ml-auto text-sm text-gray-600 hover:underline">{preview ? '退出预览' : '预览'}</button>
      </div>

      {preview && (
        <div className="bg-gray-50 rounded p-4 text-sm space-y-2 border">
          <div className="text-xs text-gray-400">预览效果（用户端）</div>
          <h2 className="text-lg font-semibold">{form.title}</h2>
          {form.summary && <p className="text-gray-600">{form.summary}</p>}
          {form.media_type === 'video' && videos.find(v => v.is_primary) && (
            <video controls src={videos.find(v => v.is_primary)!.file_url} className="w-full rounded" />
          )}
          {form.media_type === 'audio' && audios.find(a => a.is_primary) && (
            <audio controls src={audios.find(a => a.is_primary)!.file_url} className="w-full" />
          )}
          {form.body && <div className="content-body prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.body) }} />}
        </div>
      )}
        </div>
      </div>
    </div>
  )
}

const Field = ({ label, required, colSpan, hidden, hint, children, as = 'label' }: { label: string; required?: boolean; colSpan?: number; hidden?: boolean; hint?: string; children: React.ReactNode; as?: 'label' | 'div' }) => {
  if (hidden) return null
  /**
   * 外壳标签。默认用 `<label>`：点标题即可聚焦里面的单个输入框（体验更好）。
   *
   * ⚠️ 但内容若是「一组控件」，必须传 `as="div"` —— 例如富文本编辑器
   * （内含工具栏 `<button>` + 隐藏的文件输入）。原因：
   * `<label>` 没有 `for` 属性时，浏览器会把**第一个 labelable 后代**
   * （button / input / select / textarea / meter / output / progress）
   * 当作自己的关联控件，点击 label 内**任意非交互区域**都会激活它。
   * 于是在正文里点一下空白，就等于点了「导入 Word」按钮 → 弹出选择文件框。
   * 回归验证见 `tools/_check_editor_filepicker.mjs`。
   */
  const Tag: React.ElementType = as
  return (
    <Tag className={`text-sm ${colSpan === 3 ? 'md:col-span-3' : ''}`}>
      <span className="text-gray-700 mb-1 inline-flex items-center gap-1">{label}{required && <span className="text-red-500">*</span>}{hint && <span className="text-[11px] text-gray-400">{hint}</span>}</span>
      {children}
    </Tag>
  )
}

const CoverUploader = ({ item, onUpload, onRemove, onExtract, canExtract, extracting = false }: {
  item: ContentAttachment | null
  onUpload: (file: File) => void
  onRemove: () => void
  /** 提取正文第一张图作为封面 */
  onExtract: () => void
  /** 正文里是否存在图片（没有则按钮置灰） */
  canExtract: boolean
  /** 提取中（需要一次「认领」请求，给按钮加个转圈反馈） */
  extracting?: boolean
}) => (
  <div className="bg-white border rounded-lg p-3">
    <div className="text-sm text-gray-700 mb-2">封面图 <span className="text-xs text-gray-400">（用于列表页、精选推荐和专题活动卡片）</span></div>
    <div className="flex items-start gap-3">
      {item ? (
        <div className="relative w-52 h-28 border rounded overflow-hidden bg-gray-50">
          <img src={item.file_url} alt="封面图" className="w-full h-full object-cover" />
          <span className="absolute left-1 top-1 bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded">当前封面</span>
          <button type="button" onClick={onRemove} className="absolute right-1 top-1 bg-white/90 rounded p-1 text-gray-600 hover:text-red-600"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <div className="w-52 h-28 border border-dashed rounded flex items-center justify-center text-xs text-gray-400 bg-gray-50">暂未设置封面图</div>
      )}
      <div className="flex flex-col gap-2">
        <label className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded cursor-pointer hover:bg-gray-50">
          <Upload className="w-4 h-4" /> {item ? '更换封面' : '上传封面'}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.currentTarget.value = '' }} />
        </label>
        <button
          type="button"
          onClick={onExtract}
          disabled={!canExtract || extracting}
          title={canExtract ? '把正文里第一张图设为封面（文件已在服务器，只补登记附件记录，不会重复上传）' : '正文里还没有图片'}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageDown className="w-4 h-4" />}
          {extracting ? '提取中…' : '提取正文首图'}
        </button>
      </div>
    </div>
    <p className="text-[11px] text-gray-400 mt-2">
      支持 JPG、PNG、WebP，最大 5MB。正文里已有图片时，可直接点「提取正文首图」设为封面；
      导入 Word 或正文插图后，封面为空会自动取正文第一张图。
    </p>
  </div>
)

const AttachmentUploader = ({ label, type, items, onUpload, onRemove }: any) => {
  const accept = type === 'image' ? 'image/jpeg,image/png,image/webp' : type === 'video' ? 'video/mp4' : 'audio/mpeg,audio/mp3,audio/aac'
  return (
    <div>
      <div className="text-sm text-gray-700 mb-2">{label}</div>
      <label className="inline-flex items-center gap-1 px-3 py-2 text-sm border rounded cursor-pointer hover:bg-gray-50">
        <Upload className="w-4 h-4" /> 上传
        <input type="file" accept={accept} className="hidden" onChange={e => {
          const f = e.target.files?.[0]; if (f) onUpload(f)
        }} />
      </label>
      {items.length > 0 && (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {items.map((it: ContentAttachment) => (
            <div key={it.id} className="relative border rounded p-2 text-xs">
              {type === 'image' && <img src={it.file_url} alt={it.file_name} className="w-full h-24 object-cover rounded" />}
              {type === 'video' && <video src={it.file_url} className="w-full h-24 rounded" controls={false} />}
              {type === 'audio' && <audio src={it.file_url} className="w-full" controls />}
              <div className="mt-1 truncate">{it.file_name}</div>
              <div className="text-gray-400">{(it.file_size / 1024).toFixed(1)} KB</div>
              {it.is_primary ? <span className="absolute top-1 left-1 bg-blue-600 text-white text-[10px] px-1 rounded">主</span> : null}
              <button onClick={() => onRemove(it.id)} className="absolute top-1 right-1 bg-white/80 rounded p-0.5"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ContentEditor
